import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RowDataBoundEventArgs, RowSelectingEventArgs, GridComponent, ColumnsDirective as GridColumnsDirective, ColumnDirective as GridColumnDirective } from '@syncfusion/ej2-react-grids';
import { ListViewComponent } from '@syncfusion/ej2-react-lists';
import { DialogComponent, TooltipComponent, Position } from '@syncfusion/ej2-react-popups';
import { TreeGridComponent, ColumnsDirective as TreeColumnsDirective, ColumnDirective as TreeColumnDirective } from '@syncfusion/ej2-react-treegrid';

import * as Inovx from '@shared';
import ContextMenu from '@facility/viewers/components/ContextMenu';
import FacilityStore from '../../stores/FacilityStore';
import TaxonomyActions from '../../actions/TaxonomyActions';
import TaxonomyServices from '../../services/TaxonomyServices';
import TaxonomyConstants from '../../constants/TaxonomyConstants';
import RoleServices from '../../services/RoleServices';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';

var SystemStore = Inovx.Shared.System.Store;
var SystemActions = Inovx.Shared.System.Actions;
var InovxControls = Inovx.Shared.Controls;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

function getIsActionInProgress(): boolean {
    return SystemStore.isActionInProgress(TaxonomyConstants.TAXONOMY_UPDATE_EDIT);
}

type KnowledgeLibraryTreeNodeType = 'taxonomy' | 'rootTag' | 'tag';

type TagPermission = 'None' | 'View' | 'Modify' | 'Manage';

interface IKnowledgeLibraryTreeNode {
    TreeNodeKey: string;
    ID: string;
    TaxonomyID: string;
    Name: string;
    NodeType: KnowledgeLibraryTreeNodeType;
    ParentID?: string | null;
    Children?: IKnowledgeLibraryTreeNode[];
    IsExpanded?: boolean;
}

interface ISelectedKnowledgeLibraryTreeNode {
    TreeNodeKey: string;
    ID: string;
    TaxonomyID: string;
    NodeType: KnowledgeLibraryTreeNodeType;
}

interface ITreeContextMenuState {
    visible: boolean;
    left: number;
    top: number;
    node: IKnowledgeLibraryTreeNode | null;
}

interface IRolePermissionMenuState {
    visible: boolean;
    left: number;
    top: number;
    roleId: string | null;
}

interface IRolePermissionRow {
    role: IOrganizationRole;
    permission: TagPermission;
}

function createTaxonomyTreeNodeKey(taxonomyId: string): string {
    return `taxonomy:${taxonomyId}`;
}

function createTagTreeNodeKey(tagId: string): string {
    return `tag:${tagId}`;
}

function createTaxonomySelection(taxonomyId: string): ISelectedKnowledgeLibraryTreeNode {
    return {
        TreeNodeKey: createTaxonomyTreeNodeKey(taxonomyId),
        ID: taxonomyId,
        TaxonomyID: taxonomyId,
        NodeType: 'taxonomy'
    };
}

function createTreeSelection(node: IKnowledgeLibraryTreeNode): ISelectedKnowledgeLibraryTreeNode {
    return {
        TreeNodeKey: node.TreeNodeKey,
        ID: node.ID,
        TaxonomyID: node.TaxonomyID,
        NodeType: node.NodeType
    };
}

function isCannotDeleteError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && 'handled' in error
        && (error as { handled?: boolean }).handled === false;
}

function buildKnowledgeLibraryTagTree(
    tags: IClassificationTag[],
    taxonomyId: string,
    expandedTreeNodeKeys: string[]
): IKnowledgeLibraryTreeNode[] {
    const nodeById = new Map<string, IKnowledgeLibraryTreeNode>();
    const rootNodes: IKnowledgeLibraryTreeNode[] = [];

    const sortedTags = [...tags].sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));

    sortedTags.forEach((tag) => {
        nodeById.set(tag.ID, {
            TreeNodeKey: createTagTreeNodeKey(tag.ID),
            ID: tag.ID,
            TaxonomyID: taxonomyId,
            Name: tag.Name,
            NodeType: tag.ParentID ? 'tag' : 'rootTag',
            ParentID: tag.ParentID,
            Children: [],
            IsExpanded: expandedTreeNodeKeys.includes(createTagTreeNodeKey(tag.ID))
        });
    });

    sortedTags.forEach((tag) => {
        const currentNode = nodeById.get(tag.ID)!;
        if (tag.ParentID) {
            const parentNode = nodeById.get(tag.ParentID);
            if (parentNode) {
                parentNode.Children!.push(currentNode);
            }
        } else {
            rootNodes.push(currentNode);
        }
    });

    return rootNodes;
}

function collectTagIdsToDelete(node: IKnowledgeLibraryTreeNode): string[] {
    const ids = [node.ID];
    if (node.Children?.length) {
        node.Children.forEach((child) => {
            ids.push(...collectTagIdsToDelete(child));
        });
    }

    return ids;
}

function getSiblingNames(tags: IClassificationTag[], parentId: string | null, excludeId?: string): string[] {
    return tags
        .filter((tag) => tag.ParentID === parentId && tag.ID !== excludeId)
        .map((tag) => tag.Name.toLowerCase());
}

function getTagPermission(tag: IClassificationTag | null, roleId: string): TagPermission {
    if (!tag) {
        return 'None';
    }

    if (tag.ManageRoleIDs?.includes(roleId)) {
        return 'Manage';
    }

    if (tag.ModifyRoleIDs?.includes(roleId)) {
        return 'Modify';
    }

    if (tag.ViewRoleIDs?.includes(roleId)) {
        return 'View';
    }

    return 'None';
}

function setTagPermission(tag: IClassificationTag, roleId: string, permission: TagPermission): Partial<IClassificationTag> {
    const viewRoleIDs = new Set(tag.ViewRoleIDs || []);
    const modifyRoleIDs = new Set(tag.ModifyRoleIDs || []);
    const manageRoleIDs = new Set(tag.ManageRoleIDs || []);

    viewRoleIDs.delete(roleId);
    modifyRoleIDs.delete(roleId);
    manageRoleIDs.delete(roleId);

    if (permission === 'View') {
        viewRoleIDs.add(roleId);
    }
    else if (permission === 'Modify') {
        modifyRoleIDs.add(roleId);
    }
    else if (permission === 'Manage') {
        manageRoleIDs.add(roleId);
    }

    return {
        ViewRoleIDs: [...viewRoleIDs],
        ModifyRoleIDs: [...modifyRoleIDs],
        ManageRoleIDs: [...manageRoleIDs]
    };
}

function getRootAncestorTag(tags: IClassificationTag[], tag: IClassificationTag): IClassificationTag {
    let currentTag = tag;

    while (currentTag.ParentID) {
        const parentTag = tags.find((item) => item.ID === currentTag.ParentID);
        if (!parentTag) {
            break;
        }

        currentTag = parentTag;
    }

    return currentTag;
}

const KnowledgeLibrary: React.FC = () => {
    const treeGridRef = useRef<TreeGridComponent | null>(null);
    const toolTipInstance = useRef<TooltipComponent>(null);
    const [libraryTaxonomyDetail, setLibraryTaxonomyDetail] = useState<ILibraryTaxonomyDetail[]>(FacilityStore.getLibraryTaxonomyDetail());
    const [selectedTaxonomy, setSelectedTaxonomy] = useState<ILibraryTaxonomyDetail | null>(FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB));
    const [selectedTreeNode, setSelectedTreeNode] = useState<ISelectedKnowledgeLibraryTreeNode | null>(
        FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB)?.ID
            ? createTaxonomySelection(FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB).ID)
            : null
    );
    const [allRoles, setAllRoles] = useState<IOrganizationRole[]>([]);
    const [isTaxonomyDialogOpen, setIsTaxonomyDialogOpen] = useState(false);
    const [isAddTagDialogOpen, setIsAddTagDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreatingRootTag, setIsCreatingRootTag] = useState(false);
    const [newTaxonomyName, setNewTaxonomyName] = useState('');
    const [newTagName, setNewTagName] = useState('');
    const [selectedTagName, setSelectedTagName] = useState('');
    const selectedTagNameRef = useRef('');
    const newlyCreatedTagIdRef = useRef<string | null>(null);
    const [hasTaxonomyError, setHasTaxonomyError] = useState(false);
    const [hasTagError, setHasTagError] = useState(true);
    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(getIsActionInProgress());
    const [actionInProgressMsg, setActionInProgressMsg] = useState<string>(InovxLoader.t('facility:form.saving'));
    const [allFacilities, setAllFacilities] = useState<IFacility[]>(FacilityStore.getAll());
    const [hasTaxonomyChanges, setHasTaxonomyChanges] = useState(FacilityStore.getHasTaxonomyChanges());
    const [taxonomyDetailsById, setTaxonomyDetailsById] = useState<Record<string, ILibraryTaxonomyDetail>>({});
    const [expandedTreeNodeKeys, setExpandedTreeNodeKeys] = useState<string[]>([]);
    const [tagParentId, setTagParentId] = useState<string | null>(null);
    const [treeContextMenu, setTreeContextMenu] = useState<ITreeContextMenuState>({ visible: false, left: -1000, top: -1000, node: null });
    const [rolePermissionMenu, setRolePermissionMenu] = useState<IRolePermissionMenuState>({ visible: false, left: -1000, top: -1000, roleId: null });
    const [tableHeight, setTableHeight] = React.useState(window.innerHeight - 430);

    React.useEffect(() => {
        const handleResize = () => {
            let newTableHeight = window.innerHeight - 430;
            if (newTableHeight !== tableHeight) {
                // Force update to re-render the component with the new table height
                if (newTableHeight < 200) {
                    newTableHeight = 200;
                }
                setTableHeight(newTableHeight);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [tableHeight]);

    const loadState = () => {
        setLibraryTaxonomyDetail(FacilityStore.getLibraryTaxonomyDetail());
        setSelectedTaxonomy(FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB));
        setIsActionInProgress(getIsActionInProgress());
        setActionInProgressMsg(InovxLoader.t('facility:form.saving'));
        setAllFacilities(FacilityStore.getAll());
        setHasTaxonomyChanges(FacilityStore.getHasTaxonomyChanges());
    };

    const systemChange = () => {
        setIsActionInProgress(getIsActionInProgress());
    };

    useEffect(() => {
        let isMounted = true;

        RoleServices.getAll()
            .then((roles) => {
                if (!isMounted) {
                    return;
                }

                setAllRoles(roles || []);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setAllRoles([]);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const kbTaxonomies = useMemo(
        () => libraryTaxonomyDetail.filter((item) => item.Type === TaxonomyType.KB),
        [libraryTaxonomyDetail]
    );

    useEffect(() => {
        if (selectedTaxonomy || kbTaxonomies.length === 0) {
            return;
        }

        let cancelled = false;
        const loadInitialTaxonomy = async () => {
            const taxonomyDetail = await TaxonomyServices.getTaxonomyDetail(kbTaxonomies[0].ID);
            if (cancelled) {
                return;
            }

            TaxonomyActions.receiveTaxonomyDetail(taxonomyDetail);
        };

        void loadInitialTaxonomy();

        return () => {
            cancelled = true;
        };
    }, [kbTaxonomies, selectedTaxonomy]);

    const activeTaxonomyDetail = useMemo(() => {
        if (selectedTreeNode?.TaxonomyID) {
            if (selectedTaxonomy?.ID === selectedTreeNode.TaxonomyID) {
                return selectedTaxonomy;
            }

            return taxonomyDetailsById[selectedTreeNode.TaxonomyID] || null;
        }

        return selectedTaxonomy;
    }, [selectedTaxonomy, selectedTreeNode?.TaxonomyID, taxonomyDetailsById]);

    const activeTaxonomyTags = activeTaxonomyDetail?.ClassificationTags || [];

    const selectedTag = useMemo(() => {
        if (!selectedTreeNode || selectedTreeNode.NodeType === 'taxonomy' || !activeTaxonomyTags.length) {
            return null;
        }

        return activeTaxonomyTags.find(tag => tag.ID === selectedTreeNode.ID) || null;
    }, [activeTaxonomyTags, selectedTreeNode]);

    const selectedTagPermissionSource = useMemo(() => {
        if (!selectedTag || !activeTaxonomyTags.length) {
            return null;
        }

        return selectedTag.ParentID
            ? getRootAncestorTag(activeTaxonomyTags, selectedTag)
            : selectedTag;
    }, [activeTaxonomyTags, selectedTag]);

    useEffect(() => {
        setSelectedTagName(selectedTag?.Name || '');
        selectedTagNameRef.current = selectedTag?.Name || '';
    }, [selectedTag?.ID, selectedTag?.Name]);

    useEffect(() => {
        if (!selectedTaxonomy?.ID) {
            setSelectedTreeNode(null);
            setSelectedTagName('');
            selectedTagNameRef.current = '';
            return;
        }

        setSelectedTreeNode((current) => {
            if (!current) {
                return createTaxonomySelection(selectedTaxonomy.ID);
            }

            if (newlyCreatedTagIdRef.current === current.ID) {
                return current;
            }

            const taxonomyStillExists = kbTaxonomies.some((taxonomy) => taxonomy.ID === current.TaxonomyID);
            if (!taxonomyStillExists) {
                return createTaxonomySelection(selectedTaxonomy.ID);
            }

            if (current.NodeType === 'taxonomy') {
                return current;
            }

            const selectedTagStillExists = activeTaxonomyTags.some((tag) => tag.ID === current.ID);
            if (!selectedTagStillExists) {
                if (newlyCreatedTagIdRef.current === current.ID) {
                    return current;
                }

                return createTaxonomySelection(activeTaxonomyDetail?.ID || selectedTaxonomy.ID);
            }

            if (newlyCreatedTagIdRef.current === current.ID) {
                newlyCreatedTagIdRef.current = null;
            }

            return current;
        });
    }, [activeTaxonomyDetail?.ID, activeTaxonomyTags, kbTaxonomies, selectedTaxonomy?.ID, selectedTaxonomy?.VersionToken]);

    useEffect(() => {
        if (!activeTaxonomyDetail?.ID) {
            return;
        }

        setTaxonomyDetailsById((current) => ({
            ...current,
            [activeTaxonomyDetail.ID]: activeTaxonomyDetail
        }));
    }, [activeTaxonomyDetail]);

    useEffect(() => {
        let isMounted = true;

        const loadMissingTaxonomyDetails = async () => {
            const missingTaxonomies = kbTaxonomies.filter((taxonomy) => !taxonomyDetailsById[taxonomy.ID]);
            if (missingTaxonomies.length === 0) {
                return;
            }

            const loadedDetails = await Promise.all(
                missingTaxonomies.map(async (taxonomy) => [taxonomy.ID, await TaxonomyServices.getTaxonomyDetail(taxonomy.ID)] as const)
            );

            if (!isMounted) {
                return;
            }

            setTaxonomyDetailsById((current) => {
                const next = { ...current };
                loadedDetails.forEach(([taxonomyId, taxonomyDetail]) => {
                    next[taxonomyId] = taxonomyDetail;
                });

                return next;
            });
        };

        void loadMissingTaxonomyDetails();

        return () => {
            isMounted = false;
        };
    }, [kbTaxonomies, selectedTaxonomy?.ID, selectedTaxonomy?.VersionToken, taxonomyDetailsById]);

    const hasSelectedTagNameChanges = selectedTag ? selectedTagNameRef.current !== (selectedTag.Name || '') : false;
    const hasPendingTaxonomyChanges = hasTaxonomyChanges || hasSelectedTagNameChanges;
    const checkForUnsavedChanges = useCallback((event: BeforeUnloadEvent) => {
        if (hasPendingTaxonomyChanges) {
            event.returnValue = InovxLoader.t('common:msgBox.browserSaveChanges');
        }
    }, [hasPendingTaxonomyChanges]);

    useEffect(() => {
        FacilityStore.addChangeListener(loadState);
        SystemStore.addChangeListener(systemChange);
        window.addEventListener('beforeunload', checkForUnsavedChanges);

        return () => {
            FacilityStore.removeChangeListener(loadState);
            SystemStore.removeChangeListener(systemChange);
            window.removeEventListener('beforeunload', checkForUnsavedChanges);
        };
    }, [checkForUnsavedChanges]);

    const taxonomyTreeDataSource = useMemo<IKnowledgeLibraryTreeNode[]>(() => {
        return kbTaxonomies.map((taxonomy) => ({
            TreeNodeKey: createTaxonomyTreeNodeKey(taxonomy.ID),
            ID: taxonomy.ID,
            TaxonomyID: taxonomy.ID,
            Name: taxonomy.Name,
            NodeType: 'taxonomy',
            Children: taxonomy.ID === selectedTaxonomy?.ID
                ? buildKnowledgeLibraryTagTree(selectedTaxonomy.ClassificationTags || [], taxonomy.ID, expandedTreeNodeKeys)
                : buildKnowledgeLibraryTagTree(taxonomyDetailsById[taxonomy.ID]?.ClassificationTags || [], taxonomy.ID, expandedTreeNodeKeys),
            IsExpanded: expandedTreeNodeKeys.includes(createTaxonomyTreeNodeKey(taxonomy.ID))
        }));
    }, [expandedTreeNodeKeys, kbTaxonomies, selectedTaxonomy, taxonomyDetailsById]);

    const roleOptions = useMemo(() => {
        return [...allRoles].sort((a, b) => a.Name.localeCompare(b.Name));
    }, [allRoles]);

    const rolePermissionRows = useMemo<IRolePermissionRow[]>(() => {
        return roleOptions.map((role) => ({
            role,
            permission: getTagPermission(selectedTagPermissionSource, role.ID)
        }));
    }, [roleOptions, selectedTagPermissionSource]);

    const permissionTooltipText = useMemo(() => {
        return [
            `<b>${InovxLoader.t('facility:form.permissionNone')}</b>: ${InovxLoader.t('facility:form.permissionNoneDescription')}`,
            `<b>${InovxLoader.t('facility:form.permissionView')}</b>: ${InovxLoader.t('facility:form.permissionViewDescription')}`,
            `<b>${InovxLoader.t('facility:form.permissionModify')}</b>: ${InovxLoader.t('facility:form.permissionModifyDescription')}`,
            `<b>${InovxLoader.t('facility:form.permissionManage')}</b>: ${InovxLoader.t('facility:form.permissionManageDescription')}`
        ].join('<br/>');
    }, []);

    const facilityList = allFacilities
        ?.filter((item) => item.KBTaxonomyID === activeTaxonomyDetail?.ID)
        .map((item) => ({ text: item.Name, id: item.ID })) || [];

    const hideTreeContextMenu = useCallback(() => {
        setTreeContextMenu((current) => current.visible ? { visible: false, left: -1000, top: -1000, node: null } : current);
    }, []);

    const hideRolePermissionMenu = useCallback(() => {
        setRolePermissionMenu((current) => current.visible ? { visible: false, left: -1000, top: -1000, roleId: null } : current);
    }, []);

    const openTreeContextMenu = useCallback((clientX: number, clientY: number, node: IKnowledgeLibraryTreeNode) => {
        setTreeContextMenu({ visible: true, left: clientX, top: clientY, node });
    }, []);

    const getTaxonomyDetailById = useCallback((taxonomyId: string) => {
        return taxonomyDetailsById[taxonomyId]
            || (selectedTaxonomy?.ID === taxonomyId ? selectedTaxonomy : null);
    }, [selectedTaxonomy, taxonomyDetailsById]);

    const ensureSelectedTaxonomy = useCallback(async (taxonomyId: string) => {
        if (selectedTaxonomy?.ID === taxonomyId) {
            return selectedTaxonomy;
        }

        const cachedTaxonomy = taxonomyDetailsById[taxonomyId];
        if (cachedTaxonomy) {
            setSelectedTaxonomy(cachedTaxonomy);
            return cachedTaxonomy;
        }

        const taxonomyDetail = await TaxonomyServices.getTaxonomyDetail(taxonomyId);
        setTaxonomyDetailsById((current) => ({
            ...current,
            [taxonomyId]: taxonomyDetail
        }));
        setSelectedTaxonomy(taxonomyDetail);
        return taxonomyDetail;
    }, [selectedTaxonomy, taxonomyDetailsById]);

    const openAddTagDialog = useCallback(async (node: IKnowledgeLibraryTreeNode) => {
        const taxonomyDetail = await ensureSelectedTaxonomy(node.TaxonomyID);
        setSelectedTreeNode(createTreeSelection(node));
        setIsCreatingRootTag(node.NodeType === 'taxonomy');
        setTagParentId(node.NodeType === 'taxonomy' ? null : node.ID);
        setNewTagName('');
        setHasTagError(true);
        //if (node.NodeType !== 'taxonomy') {
        setExpandedTreeNodeKeys((current) => current.includes(node.TreeNodeKey) ? current : [...current, node.TreeNodeKey]);
        //}
        if (taxonomyDetail) {
            setSelectedTaxonomy(taxonomyDetail);
        }
        setIsAddTagDialogOpen(true);
        const treeGrid = treeGridRef.current;
        if (treeGrid) treeGrid.refreshColumns();
    }, [ensureSelectedTaxonomy]);

    const selectFirstAvailableTaxonomy = useCallback(async () => {
        const firstTaxonomy = FacilityStore.getLibraryTaxonomyDetail().find((item) => item.Type === TaxonomyType.KB);
        if (!firstTaxonomy) {
            setSelectedTreeNode(null);
            setSelectedTaxonomy(null);
            return;
        }

        const firstTaxonomyDetail = taxonomyDetailsById[firstTaxonomy.ID] || await TaxonomyActions.getTaxonomyDetail(firstTaxonomy.ID);
        if (!firstTaxonomyDetail) {
            return;
        }

        setTaxonomyDetailsById((current) => ({
            ...current,
            [firstTaxonomyDetail.ID]: firstTaxonomyDetail
        }));
        setSelectedTaxonomy(firstTaxonomyDetail);
        setSelectedTreeNode(createTaxonomySelection(firstTaxonomyDetail.ID));
        const treeGrid = treeGridRef.current;
        if (treeGrid) treeGrid.refreshColumns();
    }, [taxonomyDetailsById]);

    const displayCannotDeleteTaxonomy = useCallback((facilityCount: number) => {
        SystemActions.displayErrorMessage({
            heading: InovxLoader.t('facility:message.cannotDeleteTaxonomyHeading'),
            message: InovxLoader.t('facility:message.cannotDeleteTaxonomyMessage', { count: facilityCount })
        });
    }, []);

    const displayCannotDeleteTaxonomyTag = useCallback(() => {
        SystemActions.displayErrorMessage({
            heading: InovxLoader.t('facility:message.cannotDeleteTaxonomyTagHeading'),
            message: InovxLoader.t('facility:message.cannotDeleteTaxonomyTagMessage'),
            messageHasTags: true
        });
    }, []);

    const onDeleteTaxonomy = useCallback(async (taxonomyId?: string) => {
        const targetTaxonomyId = taxonomyId || selectedTaxonomy?.ID;
        const taxonomy = kbTaxonomies.find((item) => item.ID === targetTaxonomyId);
        if (hasPendingTaxonomyChanges || !TaxonomyActions.verifyTaxonomyChanges() || !taxonomy) {
            return;
        }

        try {
            const validation = await TaxonomyActions.getDeleteValidation(taxonomy.ID);
            if (!validation.CanDelete) {
                displayCannotDeleteTaxonomy(validation.FacilityCount);
                return;
            }
        } catch {
            return;
        }

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmTaxomonyDelete'),
            confirmCallback: async () => {
                setActionInProgressMsg(InovxLoader.t('facility:form.deleting'));
                try {
                    await TaxonomyActions.deleteTaxonomy(taxonomy);
                    await selectFirstAvailableTaxonomy();
                } catch (error) {
                    if (isCannotDeleteError(error)) {
                        try {
                            const validation = await TaxonomyActions.getDeleteValidation(taxonomy.ID);
                            displayCannotDeleteTaxonomy(validation.FacilityCount);
                        } catch { }
                    }
                } finally {
                    setActionInProgressMsg(InovxLoader.t('facility:form.saving'));
                }
            }
        });
    }, [displayCannotDeleteTaxonomy, hasPendingTaxonomyChanges, kbTaxonomies, selectFirstAvailableTaxonomy, selectedTaxonomy?.ID]);

    const deleteTagNode = useCallback(async (node: IKnowledgeLibraryTreeNode) => {
        if (node.NodeType === 'taxonomy') {
            return;
        }

        const taxonomyDetail = getTaxonomyDetailById(node.TaxonomyID);
        if (!taxonomyDetail) {
            return;
        }
        try {
            const validation = await TaxonomyActions.getClassificationTagDeleteValidation(node.TaxonomyID, node.ID);
            if (!validation.CanDelete) {
                displayCannotDeleteTaxonomyTag();
                return;
            }
        } catch {
            return;
        }
        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmDeleteTag'),
            confirmCallback: async () => {
                const idsToDelete = collectTagIdsToDelete(node);
                const updatedTaxonomy = structuredClone(taxonomyDetail);
                updatedTaxonomy.ClassificationTags = updatedTaxonomy.ClassificationTags?.filter((tag) => !idsToDelete.includes(tag.ID)) ?? [];
                try {
                    await TaxonomyActions.persistTaxonomyClassificationDetail(updatedTaxonomy);
                    setSelectedTreeNode(createTaxonomySelection(updatedTaxonomy.ID));
                    hideTreeContextMenu();
                } catch (error) {
                    if (isCannotDeleteError(error)) {
                        displayCannotDeleteTaxonomyTag();
                    }
                }
            }
        });

    }, [displayCannotDeleteTaxonomyTag, getTaxonomyDetailById, hideTreeContextMenu]);

    const saveTaxonomy = async () => {
        const targetTaxonomyId = selectedTreeNode?.TaxonomyID || selectedTaxonomy?.ID;
        const targetTaxonomy = (targetTaxonomyId ? getTaxonomyDetailById(targetTaxonomyId) : null)
            || activeTaxonomyDetail
            || FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB);
        const updatedTaxonomy = structuredClone(targetTaxonomy);
        if (selectedTag && hasSelectedTagNameChanges) {
            updatedTaxonomy.ClassificationTags = (updatedTaxonomy.ClassificationTags || []).map((tag) =>
                tag.ID === selectedTag.ID
                    ? {
                        ...tag,
                        Name: selectedTagName
                    }
                    : tag
            );
            // TaxonomyActions.updateTaxonomyDetail(updatedTaxonomy);
        }
        await TaxonomyActions.updateClassificationTagNew(updatedTaxonomy);

        // await TaxonomyActions.updateClassificationTag(TaxonomyType.KB);
    };

    const cancel = () => {
        if (!SystemStore.isActionInProgress(TaxonomyConstants.TAXONOMY_UPDATE_EDIT) && !hasPendingTaxonomyChanges) {
            TaxonomyActions.cancelEdit(TaxonomyType.KB);
            return;
        }

        SystemActions.displaySaveChangesDialog({
            message: InovxLoader.t('common:msgBox.saveChanges'),
            confirmCallback: async () => {
                await saveTaxonomy();
            },
            cancelCallback: async () => {
                setSelectedTagName(selectedTag?.Name || '');
                selectedTagNameRef.current = selectedTag?.Name || '';
                TaxonomyActions._finishEdit(false);
                TaxonomyActions._resetTaxonomyData(TaxonomyType.KB);
                // await TaxonomyActions.cancelEdit(TaxonomyType.KB);
            }
        });
    };

    const handleTaxonomyInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const trimmedValue = value.trim();
        let hasError = true;

        if (trimmedValue.length > 0) {
            const isUnique = kbTaxonomies.every((taxonomy: ITaxonomy) => {
                if (isEditing && taxonomy.ID === selectedTaxonomy?.ID) {
                    return true;
                }
                return taxonomy.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase();
            });
            hasError = !isUnique;
        }

        setHasTaxonomyError(hasError);
        setNewTaxonomyName(value);
    };

    const updateSelectedTagDetail = (patch: Partial<IClassificationTag>) => {
        if (!activeTaxonomyDetail || !selectedTag) {
            return;
        }

        const updatedTaxonomy = structuredClone(activeTaxonomyDetail);
        updatedTaxonomy.ClassificationTags = (updatedTaxonomy.ClassificationTags || []).map(tag =>
            tag.ID === selectedTag.ID
                ? {
                    ...tag,
                    ...patch
                }
                : tag
        );

        TaxonomyActions.updateTaxonomyDetail(updatedTaxonomy);
    };

    const updateSelectedTagPermission = (roleId: string, permission: TagPermission) => {
        if (!selectedTag || selectedTag.ParentID) {
            return;
        }

        updateSelectedTagDetail(setTagPermission(selectedTag, roleId, permission));
    };

    const handleTagNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!selectedTag) {
            return;
        }

        setSelectedTagName(event.target.value);
        selectedTagNameRef.current = event.target.value;
    };

    const handleTaxonomyNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!activeTaxonomyDetail) {
            return;
        }

        const updatedTaxonomy = structuredClone(activeTaxonomyDetail);
        updatedTaxonomy.Name = event.target.value;
        TaxonomyActions.updateTaxonomyDetail(updatedTaxonomy);
    };

    const openRolePermissionMenu = (event: React.MouseEvent<HTMLButtonElement>, roleId: string) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setRolePermissionMenu({
            visible: true,
            left: rect.left + rect.width - 8,
            top: rect.bottom + 4,
            roleId
        });
    };

    const getRolePermissionContextMenuItems = () => ([
        { id: 'None', label: InovxLoader.t('facility:form.permissionNone') },
        { id: 'View', label: InovxLoader.t('facility:form.permissionView') },
        { id: 'Modify', label: InovxLoader.t('facility:form.permissionModify') },
        { id: 'Manage', label: InovxLoader.t('facility:form.permissionManage') }
    ]);

    const handleRolePermissionMenuItemClick = (itemId: string) => {
        if (!rolePermissionMenu.roleId || !selectedTag || selectedTag.ParentID) {
            return;
        }

        updateSelectedTagPermission(rolePermissionMenu.roleId, itemId as TagPermission);
        hideRolePermissionMenu();
    };

    const onAddTaxonomy = () => {
        if (hasPendingTaxonomyChanges || !TaxonomyActions.verifyTaxonomyChanges()) {
            return;
        }
        setIsEditing(false);
        setNewTaxonomyName('');
        setHasTaxonomyError(true);
        setIsTaxonomyDialogOpen(true);
    };

    const onEditTaxonomy = (taxonomyId?: string) => {
        const targetTaxonomyId = taxonomyId || selectedTaxonomy?.ID;
        const taxonomy = kbTaxonomies.find((item) => item.ID === targetTaxonomyId);
        if (!taxonomy) {
            return;
        }
        setIsEditing(true);
        setNewTaxonomyName(taxonomy.Name);
        setHasTaxonomyError(false);
        setIsTaxonomyDialogOpen(true);
    };

    const handleSaveTaxonomy = async () => {
        if (isEditing) {
            const updatedTaxonomy = structuredClone(FacilityStore.getTaxonomyClassificationDetail(TaxonomyType.KB));
            updatedTaxonomy.Name = newTaxonomyName.trim();
            await TaxonomyActions.editTaxonomy(updatedTaxonomy);
        } else {
            await TaxonomyActions.createTaxonomy(newTaxonomyName.trim(), TaxonomyType.KB, async (newTaxonomy) => {
                const newTaxonomyDetail = await TaxonomyActions.getTaxonomyDetail(newTaxonomy.ID);
                if (newTaxonomyDetail) {
                    setTaxonomyDetailsById((current) => ({
                        ...current,
                        [newTaxonomyDetail.ID]: newTaxonomyDetail
                    }));
                    setSelectedTaxonomy(newTaxonomyDetail);
                    setSelectedTreeNode(createTaxonomySelection(newTaxonomyDetail.ID));
                }
                const treeGrid = treeGridRef.current;
                if (treeGrid) {
                    treeGrid.refreshColumns();
                }
            });
        }
        setIsTaxonomyDialogOpen(false);
    };

    const handleCancelTaxonomy = () => {
        setIsTaxonomyDialogOpen(false);
    };

    const handleAddTagInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewTagName(value);

        const trimmedValue = value.trim();
        if (!trimmedValue) {
            setHasTagError(true);
            return;
        }

        const parentId = isCreatingRootTag ? null : tagParentId;
        const siblingNames = getSiblingNames(selectedTaxonomy?.ClassificationTags || [], parentId);
        setHasTagError(siblingNames.includes(trimmedValue.toLowerCase()));
    };

    const closeAddTagDialog = () => {
        setIsAddTagDialogOpen(false);
    };

    const createTag = async () => {
        if (!activeTaxonomyDetail) {
            return;
        }

        const parentId = isCreatingRootTag ? null : tagParentId;
        if (!isCreatingRootTag && !parentId) {
            return;
        }

        const newId = crypto.randomUUID();
        newlyCreatedTagIdRef.current = newId;
        const updatedTaxonomy = structuredClone(activeTaxonomyDetail);
        updatedTaxonomy.ClassificationTags = updatedTaxonomy.ClassificationTags ?? [];
        updatedTaxonomy.ClassificationTags.push({
            ID: newId,
            Name: newTagName.trim(),
            ParentID: parentId,
            VersionToken: ''
        });

        await TaxonomyActions.persistTaxonomyClassificationDetail(updatedTaxonomy);

        if (parentId) {
            setExpandedTreeNodeKeys((current) => current.includes(createTagTreeNodeKey(parentId)) ? current : [...current, createTagTreeNodeKey(parentId)]);
        }

        setSelectedTreeNode({
            TreeNodeKey: createTagTreeNodeKey(newId),
            ID: newId,
            TaxonomyID: updatedTaxonomy.ID,
            NodeType: parentId ? 'tag' : 'rootTag'
        });
        setIsAddTagDialogOpen(false);
    };

    const saveTaxonomyAction = useAsyncActionLock(handleSaveTaxonomy);
    const createTagAction = useAsyncActionLock(createTag);

    const canChangeTreeItem = () => {
        const hasSelectedTagNameChanges = selectedTag ? selectedTagNameRef.current !== (selectedTag.Name || '') : false;

        if ((hasSelectedTagNameChanges || !TaxonomyActions.verifyTaxonomyChanges()) && !SystemStore.isActionInProgress(TaxonomyConstants.TAXONOMY_UPDATE_EDIT)) {
            SystemActions.displayInfoMessage({
                message: InovxLoader.t('facility:message.savePendingChangesInfo')
            });
            return false;
        }
        return true;
    }

    const handleTreeRowSelecting = (event: RowSelectingEventArgs) => {
        if (!canChangeTreeItem()) {
            event.cancel = true;
            return;
        }

        const target = event.target as Element | null;
        if (target?.closest('.configuration-form-tree-action-button')) {
            event.cancel = true;
        }
    };

    const handleTreeRowSelected = (event: any) => {
        const node = event?.data as IKnowledgeLibraryTreeNode | undefined;
        if (!node) {
            return;
        }

        // const taxonomyTreeNodeKey = createTaxonomyTreeNodeKey(node.TaxonomyID);
        // setExpandedTreeNodeKeys((current) => current.includes(taxonomyTreeNodeKey)
        //     ? current
        //     : [...current, taxonomyTreeNodeKey]);
        void ensureSelectedTaxonomy(node.TaxonomyID);
        setSelectedTreeNode(createTreeSelection(node));
    };

    const handleTreeRowDataBound = (args: RowDataBoundEventArgs) => {
        const row = args.row as HTMLTableRowElement;
        const node = args.data as IKnowledgeLibraryTreeNode;

        if (!row || !node) {
            return;
        }

        row.classList.add('cursor-pointer');
    };

    const handleTreeExpandedStateChanged = (event: any) => {
        const node = event?.data as IKnowledgeLibraryTreeNode | undefined;
        if (!node?.TreeNodeKey) {
            return;
        }

        setExpandedTreeNodeKeys((current) => {
            if (event.name === 'expanded') {
                return current.includes(node.TreeNodeKey) ? current : [...current, node.TreeNodeKey];
            }
            if (event.name === 'collapsed') {
                return current.filter((treeNodeKey) => treeNodeKey !== node.TreeNodeKey);
            }
            return current;
        });

        if (event.name === 'expanded' && node.NodeType === 'taxonomy' && selectedTaxonomy?.ID !== node.TaxonomyID) {
            const cachedTaxonomy = taxonomyDetailsById[node.TaxonomyID];
            if (!cachedTaxonomy) {
                void TaxonomyServices.getTaxonomyDetail(node.TaxonomyID).then((taxonomyDetail) => {
                    setTaxonomyDetailsById((current) => ({
                        ...current,
                        [node.TaxonomyID]: taxonomyDetail
                    }));
                });
            }
        }
    };

    const handleTreeDataBound = () => {
        const treeGrid = treeGridRef.current;
        if (!treeGrid) {
            return;
        }

        if (newlyCreatedTagIdRef.current) {
            const records = treeGrid.getCurrentViewRecords() as IKnowledgeLibraryTreeNode[];
            const createdIndex = records.findIndex((record) => record.TreeNodeKey === createTagTreeNodeKey(newlyCreatedTagIdRef.current as string));
            if (createdIndex >= 0) {
                treeGrid.selectRow(createdIndex);
                setSelectedTreeNode(createTreeSelection(records[createdIndex]));
                newlyCreatedTagIdRef.current = null;
                return;
            }
        }

        if (!selectedTreeNode) {
            return;
        }

        const records = treeGrid.getCurrentViewRecords() as IKnowledgeLibraryTreeNode[];
        const index = records.findIndex((record) => record.TreeNodeKey === selectedTreeNode.TreeNodeKey);
        if (index >= 0) {
            treeGrid.selectRow(index);
        }
    };

    // useEffect(() => {
    //     if (!treeGridRef.current) {
    //         return;
    //     }
    //     handleTreeDataBound();
    // }, [selectedTreeNode]);
    const renderTreeNameTemplate = useCallback((node: IKnowledgeLibraryTreeNode) => {
        const iconMarkup = node.NodeType === 'taxonomy'
            ? <img className="e-treeoverviewimage" src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/Taxonomy.png`} alt={node.NodeType} style={{ width: 18, height: 18, verticalAlign: 'middle' }} />
            : <img className="e-treeoverviewimage" src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/tag.png`} alt={node.NodeType} style={{ width: 18, height: 18, verticalAlign: 'middle' }} />;

        const isBold = node.NodeType === 'taxonomy' || node.Name === 'General';

        return (
            <div className="facility-hierarchy-treegrid-name configuration-form-tree-name">
                <div className="facility-hierarchy-treegrid-name-img configuration-form-tree-name-img">
                    {iconMarkup}
                </div>
                <div className={`facility-hierarchy-treegrid-name-label configuration-form-tree-name-label${isBold ? ' font-bold' : ''}`} title={node.Name}>
                    {node.Name}
                </div>
            </div>
        );
    }, []);

    const renderTreeActionTemplate = useCallback((node: IKnowledgeLibraryTreeNode) => {
        return (
            <button
                type="button"
                className="configuration-form-tree-action-button"
                title={InovxLoader.t('facility:grid.commands')}
                onClick={(event) => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    openTreeContextMenu(rect.left + rect.width - 8, rect.bottom + 4, node);
                }}
            >
                <i className="fa fa-ellipsis-v" />
            </button>
        );
    }, [openTreeContextMenu]);

    const getTreeContextMenuItems = (node: IKnowledgeLibraryTreeNode) => {
        if (node.NodeType === 'taxonomy') {
            return [
                { id: 'addRootTag', label: InovxLoader.t('facility:form.addRootTagTitle') },
                { id: 'deleteTaxonomy', label: InovxLoader.t('facility:form.deleteTaxonomyTitle') }
            ];
        }

        return [
            { id: 'addChildTag', label: InovxLoader.t('facility:form.addChildTag') },
            { id: 'deleteTag', label: node.NodeType === 'rootTag' ? InovxLoader.t('facility:form.deleteRootTag') : InovxLoader.t('facility:form.deleteTag') }
        ];
    };

    const renderRolePermissionValue = (permission: TagPermission) => {
        if (permission === 'None') {
            return <span className="text-muted">{InovxLoader.t('facility:form.permissionNone')}</span>;
        }

        return permission;
    };

    const renderRolePermissionAction = (row: IRolePermissionRow) => {
        return (
            <button
                type="button"
                className="configuration-form-tree-action-button"
                title={InovxLoader.t('facility:form.editSetPermission')}
                onClick={(event) => openRolePermissionMenu(event, row.role.ID)}
            >
                <i className="fa fa-ellipsis-v" />
            </button>
        );
    };

    const handleTreeContextMenuItemClick = async (itemId: string) => {
        if (!canChangeTreeItem()) return;
        const activeNode = treeContextMenu.node;
        hideTreeContextMenu();

        if (!activeNode) {
            return;
        }

        if (itemId === 'addRootTag') {
            await openAddTagDialog(activeNode);
            return;
        }

        if (itemId === 'editTaxonomy') {
            await ensureSelectedTaxonomy(activeNode.TaxonomyID);
            setSelectedTreeNode(createTaxonomySelection(activeNode.TaxonomyID));
            onEditTaxonomy(activeNode.TaxonomyID);
            return;
        }

        if (itemId === 'deleteTaxonomy') {
            onDeleteTaxonomy(activeNode.TaxonomyID);
            return;
        }

        if (itemId === 'addChildTag') {
            await openAddTagDialog(activeNode);
            return;
        }

        if (itemId === 'deleteTag') {
            await deleteTagNode(activeNode);
        }
    };
    return (
        <div className="knowledgelibrary-form facility-hierarchy-form configuration-form-shell">
            <InovxControls.StaticHeaderForm
                title={InovxLoader.t('facility:header.taxonomy')}
                saveOnClick={saveTaxonomy}
                saveBtnId="configform-save-btn"
                cancelOnClick={cancel}
                cancelBtnId="configform-cancel-btn"
                isActionInProgress={isActionInProgress}
                inProgressMessage={actionInProgressMsg}
                saveDisabled={!hasPendingTaxonomyChanges}
                cancelDisabled={!hasPendingTaxonomyChanges}
            >
                <div className="kl-form-panel-header">
                    <button type="button" className="configuration-form-add-primary-btn plus-btn" onClick={onAddTaxonomy} title={InovxLoader.t('facility:form.addKnowledgeLibraryTaxonomyTitle')}>
                        <span className="plus-btn-icon"><i className="fa fa-plus" /></span>
                        <div className="configuration-form-panel-title">{InovxLoader.t('facility:form.addKnowledgeLibraryTaxonomyTitle')}</div>
                    </button>
                </div>
                <div className="configuration-form-panels">
                    <div className="configuration-form-left-panel">
                        <TooltipComponent
                            ref={toolTipInstance}
                            target=".facility-hierarchy-treegrid-name-label"
                            beforeRender={args => {
                                const el = args.target as HTMLElement;
                                if (!el) {
                                    args.cancel = true;
                                    return;
                                }

                                // Show tooltip only if text is truncated
                                if (el.scrollWidth <= el.clientWidth) {
                                    args.cancel = true;
                                }
                            }}
                            showTipPointer={false}
                            cssClass="knowledge-view-name-tooltip"
                            style={{ height: '100%' }}
                        >
                            <TreeGridComponent
                                ref={treeGridRef}
                                className="configuration-form-treegrid"
                                dataSource={taxonomyTreeDataSource}
                                childMapping="Children"
                                expandStateMapping="IsExpanded"
                                treeColumnIndex={0}
                                height="100%"
                                rowHeight={36}
                                selectionSettings={{ cellSelectionMode: 'Flow', mode: 'Row', type: 'Single', enableToggle: false }}
                                expanded={handleTreeExpandedStateChanged}
                                collapsed={handleTreeExpandedStateChanged}
                                rowSelecting={handleTreeRowSelecting}
                                rowSelected={handleTreeRowSelected}
                                rowDataBound={handleTreeRowDataBound}
                                dataBound={handleTreeDataBound}
                                locale={InovxLoader.getSyncfusionJS2Locale() || undefined}
                            >
                                <TreeColumnsDirective>
                                    <TreeColumnDirective field="Name" headerText={InovxLoader.t('facility:form.taxonomy')} width="240" template={renderTreeNameTemplate} />
                                    <TreeColumnDirective field="TreeNodeKey" headerText="TreeNodeKey" visible={false} isPrimaryKey={true} />
                                    <TreeColumnDirective field="Actions" headerText="" width="40" template={renderTreeActionTemplate} />
                                </TreeColumnsDirective>
                            </TreeGridComponent>
                        </TooltipComponent>
                    </div>

                    <div className="configuration-form-right-panel">
                        <div className="taxonomy-details-panel">
                            <div className="d-flex align-items-center" style={{ gap: '8px', marginBottom: '14px', color: '#1a7fb8', fontSize: '18px', fontWeight: 600 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/${selectedTag ? 'icons/tag.png' : 'KB/Taxonomy.png'}`} alt={InovxLoader.t('facility:form.taxonomyTagIcon')} style={{ width: 22, height: 22 }} />
                                </span>
                                <span>
                                    {selectedTag
                                        ? (!selectedTag.ParentID
                                            ? InovxLoader.t('facility:form.rootTagPermissionsTitle')
                                            : InovxLoader.t('facility:form.childTagPermissionsTitle'))
                                        : InovxLoader.t('facility:form.taxonomyPermissionsTitle')}
                                </span>
                            </div>

                            {selectedTag ? (
                                <>
                                    <InovxControls.FormField title={InovxLoader.t('facility:form.tagName')} inputName="ClassificationName">
                                        <input
                                            type="text"
                                            value={selectedTagName}
                                            onChange={handleTagNameChange}
                                            className="form-control responsive-control"
                                            placeholder={InovxLoader.t('facility:form.classificationPlaceholder')}
                                            autoComplete="off"
                                        />
                                    </InovxControls.FormField>
                                    <div className="configuration-form-panel-title mt-1 d-flex">
                                        {selectedTag.ParentID
                                            ? InovxLoader.t('facility:form.inheritedOrganizationRolePermissions')
                                            : InovxLoader.t('facility:form.organizationRolePermissions')}
                                        <TooltipComponent content={permissionTooltipText} position={'RightCenter'}>
                                            <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/InfoBlue.png`} alt={InovxLoader.t('facility:form.informationIcon')} style={{ width: 18, height: 18, marginLeft: 8 }} />
                                        </TooltipComponent>
                                    </div>
                                    <div className="taxonomy-grid">
                                        <GridComponent
                                            className="role-permission-grid mt-1"
                                            dataSource={rolePermissionRows}
                                            width="100%"
                                            height={tableHeight}
                                            allowSelection={false}
                                            allowSorting={false}
                                            allowFiltering={false}
                                            rowHeight={34}
                                        >
                                            <GridColumnsDirective>
                                                <GridColumnDirective field="role.Name" headerText={InovxLoader.t('facility:form.organizationRole')} width="65%" template={(row: IRolePermissionRow) => row.role.Name} />
                                                <GridColumnDirective field="permission" headerText={InovxLoader.t('facility:form.permission')} width="25%" template={(row: IRolePermissionRow) => renderRolePermissionValue(row.permission)} />
                                                {!selectedTag.ParentID && (
                                                    <GridColumnDirective headerText="" width="50px" textAlign="Center" allowSorting={false} allowFiltering={false} template={renderRolePermissionAction as any} />
                                                )}
                                            </GridColumnsDirective>
                                        </GridComponent>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <InovxControls.FormField title={InovxLoader.t('facility:form.taxonomyName')} inputName="SelectedTaxonomyName">
                                        <input
                                            type="text"
                                            className="form-control responsive-control"
                                            value={selectedTaxonomy?.Name || ''}
                                            onChange={handleTaxonomyNameChange}
                                            autoComplete="off"
                                        />
                                    </InovxControls.FormField>
                                    <div className="form-field mt-1">
                                        <label htmlFor="FacilityList" className="control-label"><b>{InovxLoader.t('facility:form.facilityList')}</b> - {InovxLoader.t('facility:form.facilitiesUsedByTaxonomy')}</label>
                                        <div className="responsive-facility-list">
                                            <ListViewComponent height={220} readOnly={true} disabled={true} id="facility-list-flat" dataSource={facilityList} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {treeContextMenu.visible && treeContextMenu.node && (
                    <ContextMenu
                        left={treeContextMenu.left}
                        top={treeContextMenu.top}
                        items={getTreeContextMenuItems(treeContextMenu.node)}
                        onItemClick={handleTreeContextMenuItemClick}
                        onRequestClose={hideTreeContextMenu}
                    />
                )}
                {rolePermissionMenu.visible && rolePermissionMenu.roleId && (
                    <ContextMenu
                        left={rolePermissionMenu.left}
                        top={rolePermissionMenu.top}
                        items={getRolePermissionContextMenuItems()}
                        onItemClick={handleRolePermissionMenuItemClick}
                        onRequestClose={hideRolePermissionMenu}
                    />
                )}
            </InovxControls.StaticHeaderForm>
            {isAddTagDialogOpen && (
                <DialogComponent
                    target={'#app'}
                    width="350px"
                    showCloseIcon={true}
                    visible={true}
                    isModal={true}
                    zIndex={110000}
                    header={() => (
                        <div>
                            <div className="e-icon-settings dlg-template">{InovxLoader.t('facility:form.addClassificationTag')}</div>
                        </div>
                    )}
                    footerTemplate={() => (
                        <div>
                            <div className="d-flex j-cc">
                                <div>
                                    <a id="knowledge-library-save-tag-btn" aria-disabled={createTagAction.isInFlight} onClick={createTagAction.isInFlight ? undefined : () => void createTagAction.run()} className={`btn btn-primary${hasTagError || createTagAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                                </div>
                                <div className="ml-1">
                                    <a id="knowledge-library-cancel-tag-btn" aria-disabled={createTagAction.isInFlight} onClick={createTagAction.isInFlight ? undefined : closeAddTagDialog} className={`btn btn-default${createTagAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                                </div>
                            </div>
                        </div>
                    )}
                    close={closeAddTagDialog}
                >
                    <div className="dialogContent">
                        <InovxControls.FormField title={InovxLoader.t('facility:form.classificationName')} inputName="ClassificationName" className={hasTagError ? 'has-error' : ''}>
                            <input
                                type="text"
                                onChange={handleAddTagInputChange}
                                value={newTagName}
                                className="form-control"
                                name="ClassificationName"
                                placeholder={InovxLoader.t('facility:form.classificationPlaceholder')}
                                autoComplete="off"
                            />
                        </InovxControls.FormField>
                    </div>
                </DialogComponent>
            )}
            {isTaxonomyDialogOpen && (
                <DialogComponent
                    target={'#app'}
                    width="350px"
                    showCloseIcon={true}
                    visible={true}
                    isModal={true}
                    zIndex={110000}
                    header={() => (
                        <div>
                            <div className="e-icon-settings dlg-template">
                                {isEditing ? InovxLoader.t('facility:form.editTaxonomyTitle') : InovxLoader.t('facility:form.addTaxonomyTitle')}
                            </div>
                        </div>
                    )}
                    footerTemplate={() => (
                        <div>
                            <div className="d-flex j-cc">
                                <div>
                                    <a id="configform-save-schema-btn" aria-disabled={saveTaxonomyAction.isInFlight} onClick={saveTaxonomyAction.isInFlight ? undefined : () => void saveTaxonomyAction.run()} className={`btn btn-primary${hasTaxonomyError || saveTaxonomyAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                                </div>
                                <div className="ml-1">
                                    <a id="configform-cancel-schema-btn" aria-disabled={saveTaxonomyAction.isInFlight} onClick={saveTaxonomyAction.isInFlight ? undefined : handleCancelTaxonomy} className={`btn btn-default${saveTaxonomyAction.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                                </div>
                            </div>
                        </div>
                    )}
                    close={handleCancelTaxonomy}
                >
                    <div className="dialogContent">
                        <InovxControls.FormField title={InovxLoader.t('facility:form.taxonomyName')} inputName="TaxonomyName" className={hasTaxonomyError ? 'has-error' : ''}>
                            <input
                                type="text"
                                onChange={handleTaxonomyInputChange}
                                value={newTaxonomyName}
                                className="form-control"
                                name="TaxonomyName"
                                placeholder={InovxLoader.t('facility:form.taxonomyNamePlaceholder')}
                                autoComplete="off"
                            />
                        </InovxControls.FormField>
                    </div>
                </DialogComponent>
            )}
        </div>
    );
};

export default KnowledgeLibrary;
